import React, { useState, useCallback, useMemo } from "react";
import cn from "clsx";
import range from "lodash/range";
import {
  YearMonthDay,
  daysInMonth,
  convertStringToYearMonthDay,
  convertDateToString
} from "smoldate";
import { InputCustomChangeEventHandler } from "../../types";
import {
  get,
  hasAndIsNotEmpty,
  calcTotalValidProperties
} from "../../utils/helpers";
import { ISO3_TO_ISO2 } from "../../utils/countries";
import FieldLabel from "../Field/Label";
import InputSelectCustom, {
  ReactSelectOptionObject
} from "../InputSelectCustom";
import "./InputYearMonthDay.css";

export interface InputYearMonthDayProps {
  /** The ID of the input. */
  id: string;
  /** The name of the input. */
  name: string;
  /** The input's value as a string in YYYY-MM-DD format. */
  value: string | null;
  /** The labels to output for each child input. */
  labels?: {
    year?: string;
    yearOptionNotSet?: string;
    month?: string;
    monthOptionNotSet?: string;
    day?: string;
    dayOptionNotSet?: string;
  };
  /**
   * The starting year to use when generating year options.
   *
   * @default new Date().getUTCFullYear()
   */
  startYear?: number;
  /**
   * The maximum number of years to display.
   *
   * @default 120
   */
  numYears?: number;
  /** For localisation ordering you can set the ISO 3166-1 Alpha 2 code (uppercase) */
  country?: string;
  /**
   * For localisation ordering you can set the BCP 47 language code.
   * It's important to note that the country value will be prioritised over the language value.
   */
  lang?: string;
  /** Boolean to control if child inputs are searchable. */
  searchable?: boolean;
  /** Boolean to control if child inputs are clearable. */
  clearable?: boolean;
  /** Boolean to control if input is disabled. */
  disabled?: boolean;
  /** Extra class names to assign to the input. */
  className?: string;
  /** Event handler for when a child input is focused. */
  onFocus?: React.FocusEventHandler;
  /** Event handler for when a child input is blurred. */
  onBlur?: React.FocusEventHandler;
  /**
   * Custom event handler for when the input's value is changed.
   *
   * Please note that this fires after the child's input change event has
   * fired and publishes the final value, e.g. changing a child input would
   * output a full YYYY-MM-DD as the value.
   */
  onChange?: InputCustomChangeEventHandler;
}

export type InputYearMonthDayFields = "year" | "month" | "day";

export default function InputYearMonthDay({
  id,
  name,
  value,
  labels = {},
  className,
  startYear,
  numYears = 120,
  lang,
  country,
  searchable = true,
  clearable,
  disabled,
  onFocus,
  onBlur,
  onChange
}: InputYearMonthDayProps) {
  // Shared/internal context/state
  const [focused, setFocused] = useState({
    year: false,
    month: false,
    day: false
  });
  // Memoized values
  // html attributes for country and language
  // these defines the order by css (InputYearMonthDay.css)
  const l10nAttributes = useMemo(() => {
    // Prioritise the country over the language
    if (country) {
      if (country.length === 3) {
        country = ISO3_TO_ISO2[country.toUpperCase()];
      }
      return { "data-country": country };
    }

    // If country not set, use language
    if (lang) {
      return { lang: lang };
    }
  }, [lang, country]);

  const _startYear = useMemo(
    () => startYear || new Date().getUTCFullYear() + 1,
    [startYear]
  );

  const currentValue = useMemo(() => convertStringToYearMonthDay(value), [
    value
  ]);

  const isFocused = useMemo(() => calcTotalValidProperties(focused) > 0, [
    focused
  ]);

  const yearOptions = useMemo<ReactSelectOptionObject[]>(
    () =>
      range(numYears + 1).map(i => ({
        label: `${
          i
            ? _startYear - i
            : hasAndIsNotEmpty(labels, "yearOptionNotSet")
            ? get(labels, "yearOptionNotSet")
            : " "
        }`,
        value: i ? _startYear - i : 0
      })),
    [_startYear, numYears]
  );

  const monthOptions = useMemo<ReactSelectOptionObject[]>(
    () =>
      range(13).map(i => ({
        label: `${
          i
            ? i
            : hasAndIsNotEmpty(labels, "monthOptionNotSet")
            ? get(labels, "monthOptionNotSet")
            : " "
        }`,
        value: i
      })),
    [currentValue.year]
  );

  const dayOptions = useMemo<ReactSelectOptionObject[]>(
    () =>
      range(daysInMonth(currentValue.year, currentValue.month) + 1).map(i => ({
        label: `${
          i
            ? i
            : hasAndIsNotEmpty(labels, "dayOptionNotSet")
            ? get(labels, "dayOptionNotSet")
            : " "
        }`,
        value: i
      })),
    [currentValue.year, currentValue.month]
  );

  // Event handlers
  // -- Fire the custom event handler to publish the new value
  const handleChange = useCallback(
    (newValue: YearMonthDay) => {
      if (onChange) {
        onChange(convertDateToString(newValue), name);
      }
    },
    [name, onChange]
  );

  // -- Set each individual subfield focused
  const handleFocusInputSelectCustomFn = useCallback<
    (fieldKey: InputYearMonthDayFields) => React.FocusEventHandler
  >(
    fieldKey => (event: React.FocusEvent) => {
      const updateFocused = { year: false, month: false, day: false };
      setFocused({ ...updateFocused, [fieldKey]: true });
      if (onFocus) {
        onFocus(event);
      }
    },
    [focused, onFocus]
  );

  // -- Set each individual subfield unfocused
  const handleBlurInputSelectCustom = useCallback<React.FocusEventHandler>(
    (event: React.FocusEvent) => {
      setFocused({ year: false, month: false, day: false });
      if (onBlur) {
        onBlur(event);
      }
    },
    [focused, onFocus]
  );

  // -- When each individual field changes, it should fire the
  //    custom handleChange to publish the changes upward
  const handleChangeInputSelectCustomFn = useCallback<
    (fieldKey: InputYearMonthDayFields) => InputCustomChangeEventHandler
  >(
    fieldKey => newValue => {
      const updateCurrentValue = { ...currentValue };
      updateCurrentValue[fieldKey] = Number(newValue) || 0;
      handleChange(updateCurrentValue);
    },
    [currentValue]
  );

  return (
    <div
      {...l10nAttributes}
      className={cn(
        "input-yearmonthday",
        !!value && "has-value",
        isFocused && "is-focused",
        disabled && "is-disabled",
        className
      )}
    >
      <div
        className={cn(
          "subfield input-year",
          !!labels.year && "has-label",
          currentValue.year > 0 && "has-value",
          focused.year && "is-focused"
        )}
      >
        {hasAndIsNotEmpty(labels, "year") && (
          <FieldLabel
            id={`${id}-year`}
            label={labels.year}
            className="subfield-label"
          />
        )}
        <InputSelectCustom
          className="subfield-input"
          id={`${id}-year`}
          name="year"
          value={currentValue.year || null}
          options={yearOptions}
          sortOptionsAlphabetically={false}
          clearable={clearable}
          searchable={searchable}
          disabled={disabled}
          onFocus={handleFocusInputSelectCustomFn("year")}
          onBlur={handleBlurInputSelectCustom}
          onChange={handleChangeInputSelectCustomFn("year")}
        />
      </div>
      <div
        className={cn(
          "subfield input-month",
          !!labels.month && "has-label",
          currentValue.month > 0 && "has-value",
          focused.month && "is-focused"
        )}
      >
        {hasAndIsNotEmpty(labels, "month") && (
          <FieldLabel
            id={`${id}-month`}
            label={labels.month}
            className="subfield-label"
          />
        )}
        <InputSelectCustom
          className="subfield-input"
          id={`${id}-month`}
          name="month"
          value={currentValue.month || null}
          options={monthOptions}
          sortOptionsAlphabetically={false}
          clearable={clearable}
          searchable={searchable}
          disabled={disabled}
          onFocus={handleFocusInputSelectCustomFn("month")}
          onBlur={handleBlurInputSelectCustom}
          onChange={handleChangeInputSelectCustomFn("month")}
        />
      </div>
      <div
        className={cn(
          "subfield input-day",
          !!labels.day && "has-label",
          currentValue.day > 0 && "has-value",
          focused.day && "is-focused"
        )}
      >
        {hasAndIsNotEmpty(labels, "day") && (
          <FieldLabel
            id={`${id}-day`}
            label={labels.day}
            className="subfield-label"
          />
        )}
        <InputSelectCustom
          className="subfield-input"
          id={`${id}-day`}
          name="day"
          value={currentValue.day || null}
          options={dayOptions}
          sortOptionsAlphabetically={false}
          clearable={clearable}
          searchable={searchable}
          disabled={disabled}
          onFocus={handleFocusInputSelectCustomFn("day")}
          onBlur={handleBlurInputSelectCustom}
          onChange={handleChangeInputSelectCustomFn("day")}
        />
      </div>
    </div>
  );
}
